import { useEffect, useState } from 'react';
import ErrorDisplay from '../../../../components/ErrorDisplay';
import { SuccessStoryPicker } from '../../../../pickers/success-story/SuccessStoryPicker';
import type { SuccessStory } from '../../../../types/success-stories';
import type { JourneyItemTemplateStoryFirestore } from '../../services/journeyTypes';
import { JourneyItemFormActions } from './JourneyItemFormActions';
import { JourneyItemFormFields } from './JourneyItemFormFields';
import { useJourneyItemForm } from './useJourneyItemForm';

interface StorySpecificFormData {
    imageUrl: string;
    successStoryId: string;
}

interface StoryJourneyItemFormProps {
    itemTemplate?: JourneyItemTemplateStoryFirestore;
    initialOrder: number;
    onSave: (item: JourneyItemTemplateStoryFirestore) => void;
    onSaveAndContinue?: (item: JourneyItemTemplateStoryFirestore) => void;
    onCancel: () => void;
}

export const StoryJourneyItemForm = ({ itemTemplate, initialOrder, onSave, onSaveAndContinue, onCancel }: StoryJourneyItemFormProps) => {
    const {
        commonFormData,
        loading,
        error,
        handleCommonChange,
        handleSubmit,
        handleSaveAndContinue,
    } = useJourneyItemForm({
        itemTemplate,
        initialOrder,
        defaultProgressType: 'fixed',
    });

    const [specificFormData, setSpecificFormData] = useState<StorySpecificFormData>({
        imageUrl: '',
        successStoryId: '',
    });

    useEffect(() => {
        if (itemTemplate && itemTemplate.id) {
            setSpecificFormData({
                imageUrl: itemTemplate.imageUrl,
                successStoryId: itemTemplate.successStoryId,
            });
        }
    }, [itemTemplate]);

    const handleChange = (field: string, value: string | number | boolean) => {
        if (field in commonFormData) {
            handleCommonChange(field as keyof typeof commonFormData, value);
        } else {
            setSpecificFormData((prev) => ({ ...prev, [field]: value }));
        }
    };

    const validateSpecificFields = (): string | null => {
        if (!specificFormData.imageUrl.trim()) {
            return 'Image URL is required for story items';
        }
        if (!specificFormData.successStoryId.trim()) {
            return 'Success Story is required for story items';
        }
        return null;
    };

    const createItem = (baseData: Partial<JourneyItemTemplateStoryFirestore>): JourneyItemTemplateStoryFirestore => {
        return {
            ...baseData,
            type: 'story',
            maxProgress: 1,
            imageUrl: specificFormData.imageUrl,
            successStoryId: specificFormData.successStoryId,
        } as JourneyItemTemplateStoryFirestore;
    };

    const onSubmit = handleSubmit(validateSpecificFields, createItem, onSave);
    const onSaveAndContinueClick = onSaveAndContinue ? handleSaveAndContinue(validateSpecificFields, createItem, onSaveAndContinue) : undefined;

    return (
        <form onSubmit={onSubmit} className="space-y-4">
            {error && <ErrorDisplay message={error} />}

            <JourneyItemFormFields
                formData={commonFormData}
                itemType="story"
                disableProgressType={true}
                onChange={handleChange}
            />

            <h2 className="text-lg font-bold mt-2">Story-specific fields</h2>
            <hr className="-mt-4" />

            <SuccessStoryPicker
                value={specificFormData.successStoryId}
                onChange={(story: SuccessStory | null) => {
                    if (story) {
                        setSpecificFormData((prev) => ({
                            ...prev,
                            successStoryId: story.id,
                            imageUrl: story.featuredImageUrl,
                        }));
                    } else {
                        setSpecificFormData((prev) => ({
                            ...prev,
                            successStoryId: '',
                            imageUrl: '',
                        }));
                    }
                }}
                label="Success Story *"
                required
            />

            {specificFormData.imageUrl && (
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Selected Story Image</label>
                    <div className="border border-gray-300 rounded-md p-4 bg-gray-50 flex justify-center items-center">
                        <img
                            src={specificFormData.imageUrl}
                            alt={specificFormData.successStoryId}
                            className="max-w-full h-auto rounded-md w-64 h-64 object-cover"
                        />
                    </div>
                </div>
            )}

            {error && <ErrorDisplay message={error} />}

            <JourneyItemFormActions
                onCancel={onCancel}
                onSaveAndContinue={onSaveAndContinueClick}
                loading={loading}
                isEditMode={!!itemTemplate?.id}
            />
        </form>
    );
};
